import { getToken } from '@josempgon/vue-keycloak';
import Axios, { AxiosError, type AxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_API_URL;

export const AXIOS_INSTANCE = Axios.create({ baseURL });

// Map to store cancel tokens for each request
export const pendingRequests = new Map();

// Create a unique key for each request to track it
const generateRequestKey = (config: AxiosRequestConfig): string => {
  const { url, method, params, data } = config;
  return `${method}_${url}_${JSON.stringify(params)}_${JSON.stringify(data)}`;
};

AXIOS_INSTANCE.interceptors.request.use(
  async (config) => {
    const token = await getToken();
    config.headers.Authorization = `Bearer ${token}`;

    // Cancel any pending request with the same identifier
    const requestKey = generateRequestKey(config);
    if (pendingRequests.has(requestKey)) {
      const previousCancelToken = pendingRequests.get(requestKey);
      // previousCancelToken.cancel(`Request canceled due to duplicate request: ${requestKey}`);
      pendingRequests.delete(requestKey);
    }

    // Create a new cancel token for this request
    const source = Axios.CancelToken.source();
    config.cancelToken = source.token;
    pendingRequests.set(requestKey, source);

    return config;
  },
  (error) => Promise.reject(error.message),
);

// Remove requests from pendingRequests after they complete
AXIOS_INSTANCE.interceptors.response.use(
  (response) => {
    const requestKey = generateRequestKey(response.config);
    pendingRequests.delete(requestKey);
    return response;
  },
  (error) => {
    // If the request was canceled, we handle it silently
    if (Axios.isCancel(error)) {
      return Promise.reject(error.message);
    }

    // For other errors, remove from pendingRequests
    if (error.config) {
      const requestKey = generateRequestKey(error.config);
      pendingRequests.delete(requestKey);
    }

    return Promise.reject(error);
  }
);

export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const source = Axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // Add cancel method to the promise
  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled by client');
  };

  return promise;
};

// Function to cancel all pending requests
export const cancelAllRequests = (message = 'Canceled by user') => {
  pendingRequests.forEach((source) => {
    source.cancel(message);
  });
  pendingRequests.clear();
};

// Function to cancel requests by URL pattern
export const cancelRequestsByUrlPattern = (urlPattern: string | RegExp, message = 'Canceled by pattern match') => {
  pendingRequests.forEach((source, key) => {
    if (typeof urlPattern === 'string' && key.includes(urlPattern)) {
      source.cancel(message);
      pendingRequests.delete(key);
    } else if (urlPattern instanceof RegExp && urlPattern.test(key)) {
      source.cancel(message);
      pendingRequests.delete(key);
    }
  });
};

// Function to cancel only query requests (GET)
export const cancelQueryRequests = (message = 'Query canceled') => {
  pendingRequests.forEach((source, key) => {
    // GET requests start with "GET_" or "get_"
    if (key.toLowerCase().startsWith('get_')) {
      source.cancel(message);
      pendingRequests.delete(key);
    }
  });
};

export type ErrorType<e> = AxiosError<e>;
export type BodyType<BodyData> = BodyData;

export const axios = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => customInstance(config, options);

export default AXIOS_INSTANCE;
